import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Typography, Button, Modal, Form, Input, Select, Tag, message } from '../../ui';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { formatIst } from '../../lib/time';

export function TemplatesPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [bOpen, setBOpen] = useState(false);
  const canBroadcast = usePermission('NOTIF.BROADCAST'); // separate from NOTIF.TEMPLATE.ADMIN (the route/page perm)

  const { data, isLoading } = useQuery({
    queryKey: ['notif-templates'],
    queryFn: () => request<any[]>({ url: '/notifications/templates' }),
  });
  const create = useMutation({
    mutationFn: (v: any) => request({ url: '/notifications/templates', method: 'POST', data: v }),
    onSuccess: () => { message.success('Saved'); setOpen(false); qc.invalidateQueries({ queryKey: ['notif-templates'] }); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save template'),
  });
  const broadcast = useMutation({
    mutationFn: (v: any) => request<any>({ url: '/notifications/broadcast', method: 'POST', data: v }),
    onSuccess: (r) => { message.success(`Queued ${r.enqueued}`); setBOpen(false); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Broadcast failed'),
  });

  // Broadcasts go to an entire audience and can't be recalled — confirm first.
  const confirmBroadcast = (v: any) =>
    Modal.confirm({
      title: 'Send this broadcast?',
      content: `This sends a ${v.channel} message to ${v.audience?.replace('ALL_', 'all ').toLowerCase()}. It cannot be undone.`,
      okText: 'Send',
      onOk: () => broadcast.mutate(v),
    });

  const columns: DataTableColumn<any>[] = [
    { key: 'code', title: 'Code', dataIndex: 'code', sortable: true, alwaysVisible: true },
    {
      key: 'channel', title: 'Channel', dataIndex: 'channel', sortable: true,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    { key: 'language', title: 'Lang', dataIndex: 'language', sortable: true, width: 80 },
    { key: 'subject', title: 'Subject', dataIndex: 'subject' },
    {
      key: 'body', title: 'Body', dataIndex: 'body',
      render: (v: string) => (
        <div style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</div>
      ),
    },
    {
      key: 'createdAt', title: 'Created', dataIndex: 'createdAt', sortable: true, defaultVisible: false,
      render: (v: string) => formatIst(v),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Notification Templates & Broadcast</Typography.Title>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button variant="primary" size="sm" onClick={() => setOpen(true)}>New Template</Button>
          {canBroadcast && <Button variant="default" size="sm" onClick={() => setBOpen(true)}>Broadcast</Button>}
        </div>
      </div>
      <Card>
        <DataTable
          storageKey="notif-templates"
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          pagination={{ pageSize: 25, showSizeChanger: true }}
        />
      </Card>

      <Modal title="New Template" open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <Form layout="vertical" onFinish={(v) => create.mutate(v)}>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}><Input placeholder="ORDER_PLACED" /></Form.Item>
          <Form.Item name="channel" label="Channel" rules={[{ required: true }]}>
            <Select options={[{ value: 'FCM' }, { value: 'WHATSAPP' }, { value: 'SMS' }, { value: 'EMAIL' }]} />
          </Form.Item>
          <Form.Item name="language" label="Language" initialValue="en"><Input /></Form.Item>
          <Form.Item name="subject" label="Subject"><Input placeholder="Optional" /></Form.Item>
          <Form.Item name="body" label="Body (Handlebars: {{var}})" rules={[{ required: true }]}>
            <Input.TextArea rows={4} placeholder="Hello {{name}}, your order {{orderNo}} of ₹{{amount}} is placed." />
          </Form.Item>
          <Button variant="primary" type="submit" block loading={create.isPending}>Save</Button>
        </Form>
      </Modal>

      <Modal title="Broadcast" open={bOpen} onOpenChange={(o) => { if (!o) setBOpen(false); }}>
        <Form layout="vertical" onFinish={confirmBroadcast}>
          <Form.Item name="audience" label="Audience" rules={[{ required: true }]}>
            <Select options={['ALL_RETAILERS', 'ALL_FARMERS', 'ALL_SALES'].map((a) => ({ value: a }))} />
          </Form.Item>
          <Form.Item name="channel" label="Channel" rules={[{ required: true }]}>
            <Select options={[{ value: 'FCM' }, { value: 'WHATSAPP' }, { value: 'SMS' }]} />
          </Form.Item>
          <Form.Item name="title" label="Title"><Input /></Form.Item>
          <Form.Item name="body" label="Body" rules={[{ required: true }]}><Input.TextArea rows={3} /></Form.Item>
          <Button variant="primary" type="submit" block loading={broadcast.isPending}>Send</Button>
        </Form>
      </Modal>
    </div>
  );
}
